import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserCheck, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function PendingSignups() {
  const { actingOrgId } = useAuth();
  const qc = useQueryClient();
  const { data: users = [], isLoading } = useQuery({
    queryKey: ["pending-signups", actingOrgId],
    queryFn: async () => (await api.get("/org/pending-signups")).data,
  });

  const act = useMutation({
    mutationFn: async ({ id, action }) => (await api.post(`/org/pending-signups/${id}/${action}`)).data,
    onSuccess: (_, v) => { toast.success(v.action === "approve" ? "Approved" : "Rejected"); qc.invalidateQueries({ queryKey: ["pending-signups"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <div>
      <PageHeader title="Pending signups" description="Employees who signed up via join link and requested admin approval" />
      {isLoading ? <p className="text-sm text-slate-500">Loading…</p> : users.length === 0 ? (
        <EmptyState title="No pending signups" hint="Approvals requested via the join link appear here" icon={UserCheck} />
      ) : (
        <Card className="divide-y divide-slate-100">
          {users.map((u) => (
            <div key={u.id} className="flex items-center justify-between px-5 py-3" data-testid={`pending-row-${u.email}`}>
              <div><p className="text-sm font-medium text-slate-800">{u.name}</p><p className="text-xs text-slate-400">{u.email}</p></div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => act.mutate({ id: u.id, action: "approve" })} data-testid={`approve-${u.email}`}>
                  <Check className="mr-1 h-4 w-4" /> Approve
                </Button>
                <Button size="sm" variant="outline" onClick={() => act.mutate({ id: u.id, action: "reject" })} data-testid={`reject-${u.email}`}>
                  <X className="mr-1 h-4 w-4" /> Reject
                </Button>
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
